// src/pages/nascar/NascarBetting.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

/** tiny classnames helper */
const cx = (...classes) => classes.filter(Boolean).join(" ");

/* ------------------------ odds helpers ------------------------ */
function probToAmerican(probPct) {
  const q = Number(probPct) / 100;
  if (!Number.isFinite(q) || q <= 0 || q >= 1) return "—";
  // Favorite (>= 50%): negative odds
  if (q >= 0.5) return String(Math.round((-100 * q) / (1 - q)));
  // Underdog (< 50%): positive odds
  return `+${Math.round((100 * (1 - q)) / q)}`;
}

function FinishPositionOU({ row, maxPos = 40, isDark }) {
  const [line, setLine] = useState(5.5);

  const dist = useMemo(() => {
    if (!row) return [];
    const cols = Object.keys(row)
      .filter((c) => /^P?\d+$/i.test(String(c)))
      .map((c) => {
        const m = String(c).match(/\d+/);
        return { c, n: m ? Number(m[0]) : 0 };
      })
      .filter((x) => x.n >= 1 && x.n <= maxPos)
      .sort((a, b) => a.n - b.n);

    return cols.map(({ c, n }) => {
      const v = Number(row?.[c]);
      return { pos: n, pct: Number.isFinite(v) ? v : 0 };
    });
  }, [row, maxPos]);

  const underPct = useMemo(() => {
    const cutoff = Math.floor(line); // 5.5 => positions 1..5 are "Under"
    return dist
      .filter((d) => d.pos <= cutoff)
      .reduce((s, d) => s + (Number.isFinite(d.pct) ? d.pct : 0), 0);
  }, [dist, line]);

  const overPct = useMemo(() => {
    const v = 100 - underPct;
    return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;
  }, [underPct]);

  const hoverUnderline =
    "cursor-pointer relative inline-block after:absolute after:left-0 after:-bottom-0.5 after:h-[2px] after:w-0 after:bg-sky-400 after:transition-all after:duration-200 hover:after:w-full";

  return (
    <div
      className={
        "mt-4 rounded-xl border p-4 " +
        (isDark ? "border-slate-800 bg-slate-950/60" : "border-slate-200 bg-white")
      }
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">Set Over/Under Line: {line.toFixed(1)}</div>
        <div className={"text-xs " + (isDark ? "text-slate-400" : "text-slate-600")}>
          Under = finish {line.toFixed(1)} or better · Over = worse than {line.toFixed(1)}
        </div>
      </div>

      <input
        type="range"
        min={1}
        max={maxPos}
        step={0.5}
        value={line}
        onChange={(e) => setLine(Number(e.target.value))}
        className="w-full accent-sky-400"
      />

      <table className="w-full mt-4 text-sm">
        <thead>
          <tr className={"border-b " + (isDark ? "border-slate-800/80" : "border-slate-200")}
          >
            <th className="text-left py-1">Type</th>
            <th className="text-center py-1">Percentage</th>
            <th className="text-right py-1">FMV</th>
          </tr>
        </thead>
        <tbody>
          <tr className={"border-b " + (isDark ? "border-slate-800/50" : "border-slate-100")}
          >
            <td className={"py-2 " + hoverUnderline}>Over</td>
            <td className="text-center">{overPct.toFixed(1)}%</td>
            <td className="text-right">{probToAmerican(overPct)}</td>
          </tr>
          <tr>
            <td className={"py-2 " + hoverUnderline}>Under</td>
            <td className="text-center">{underPct.toFixed(1)}%</td>
            <td className="text-right">{probToAmerican(underPct)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


/* ---------------- LAST UPDATED (shared) ---------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch (_) {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch (_) {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        }).then((x) => x.json());
        const iso =
          m?.updated_iso ||
          m?.updated_utc ||
          m?.updated ||
          m?.lastUpdated ||
          m?.timestamp;
        const ep = m?.updated_epoch;
        const d = iso
          ? new Date(iso)
          : Number.isFinite(ep)
          ? new Date(ep * 1000)
          : null;
        if (alive && d && !isNaN(d)) setUpdatedAt(d);
      } catch (_) {}
    })();
    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ------------------------ tiny data hook ------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    // ✅ EARLY EXIT if no URL (important)
    if (!url) {
      setData(null);
      setErr(null);
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    setLoading(true);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (!alive) return;
        setData(j);
        setErr(null);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e);
        setData(null);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err, loading };
}

/* ----------------------------- helpers ----------------------------- */
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};
const isNumeric = (v) => Number.isFinite(num(v));
const cmp = (a, b, dir = "asc", col = "") => {
  const A = getSortValue(a, col);
  const B = getSortValue(b, col);

  // always push missing values to the bottom
  if (!A.missing && B.missing) return -1;
  if (A.missing && !B.missing) return 1;

  // numeric compare
  if (typeof A.value === "number" && typeof B.value === "number") {
    return dir === "asc" ? A.value - B.value : B.value - A.value;
  }

  // text compare
  const sa = String(A.value ?? "");
  const sb = String(B.value ?? "");
  if (sa < sb) return dir === "asc" ? -1 : 1;
  if (sa > sb) return dir === "asc" ? 1 : -1;
  return 0;
};
const isDriverCol = (c) => /^driver\b/i.test(String(c));
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const escapeCSV = (v) => {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const looksNumericCol = (c) =>
  /(^rank$|avg|average|proj|odds|line|ev|edge|value|prob|%|rtg|dom|ceiling|floor|spread|total|ml$|p$)/i.test(
    String(c).trim()
  ) || /^\d+$/.test(String(c));


/* ----------------------------- formatting helpers ----------------------------- */
// Convert input to a percent string.
// - If the value already looks like "12.3%", keep it.
// - If it's a number between 0 and 1, treat as probability (0.123 -> 12.3%)
// - If it's a number between 1 and 100, treat as percent (12.3 -> 12.3%)
const toPct = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (/%$/.test(s)) return s;
  let n = num(v);
  if (!Number.isFinite(n)) return "";
  if (n <= 1) n = n * 100;
  // keep one decimal if needed
  const oneDec = Math.round(n * 10) / 10;
  return `${oneDec}%`;
};


// Parse input as a percent number (not a string).
// Mirrors toPct rules:
// - If number between 0 and 1 -> probability -> *100
// - If number between 1 and 100 -> percent as-is
const pctNumber = (v) => {
  if (v === null || v === undefined) return NaN;
  const s = String(v).trim();
  if (!s) return NaN;
  if (/%$/.test(s)) {
    const n = num(s);
    return Number.isFinite(n) ? n : NaN;
  }
  let n = num(v);
  if (!Number.isFinite(n)) return NaN;
  if (n <= 1) n = n * 100;
  return n;
};

// Parse as integer-ish (returns null if not numeric)
const asInt = (v) => {
  const n = num(v);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

// Format American odds (+xxx / -xxx)
const formatOdds = (v) => {
  const n = asInt(v);
  if (n === null) return "";
  if (n > 0) return `+${n}`;
  return `${n}`;
};

const isFairOddsCol = (col) =>
  /^fairodds$/i.test(String(col ?? "").replace(/\s+/g, ""));

const getSortValue = (value, col) => {
  if (isFairOddsCol(col)) {
    const n = num(value);

    // 0 / blank / non-numeric FairOdds = missing, always sort last
    if (!Number.isFinite(n) || n === 0) {
      return { missing: true, value: null };
    }

    return { missing: false, value: n };
  }

  const n = num(value);
  if (Number.isFinite(n)) {
    return { missing: false, value: n };
  }

  return { missing: true, value: String(value ?? "").toLowerCase() };
};

const displayFairOdds = (v) => {
  const n = num(v);
  if (!Number.isFinite(n) || n === 0) return "∞"; // or "—"
  return formatOdds(n);
};

/* ------------------------ odds + kelly helpers ------------------------ */
function parseAmericanOdds(v) {
  if (v === null || v === undefined) return NaN;
  const s = String(v).trim();
  if (!s) return NaN;
  // allow "+120", "-135", "120", "- 135"
  const n = Number(s.replace(/[^\d\-+]/g, ""));
  return Number.isFinite(n) && n !== 0 ? n : NaN;
}

function americanToImpliedProbPct(odds) {
  const o = Number(odds);
  if (!Number.isFinite(o) || o === 0) return NaN;
  if (o > 0) return (100 / (o + 100)) * 100;
  const a = Math.abs(o);
  return (a / (a + 100)) * 100;
}

function americanToDecimal(odds) {
  const o = Number(odds);
  if (!Number.isFinite(o) || o === 0) return NaN;
  if (o > 0) return 1 + o / 100;
  return 1 + 100 / Math.abs(o);
}

// Full Kelly fraction (as a fraction of bankroll, e.g. 0.03 = 3% of roll)
// f* = (b*p - q)/b where b = decimal-1, p in [0,1], q=1-p
function kellyFraction(p, decimalOdds) {
  const dec = Number(decimalOdds);
  const pp = Number(p);
  if (!(pp > 0 && pp < 1) || !(dec > 1)) return 0;
  const b = dec - 1;
  const q = 1 - pp;
  const f = (b * pp - q) / b;
  return Number.isFinite(f) ? Math.max(0, f) : 0;
}

const fmtPct1 = (n) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : "—");
const fmtPct2 = (n) => (Number.isFinite(n) ? `${n.toFixed(2)}%` : "—");


/* ---------------- heatmap rules + palettes ---------------- */
const dirForCol = (colName) => {
  const k = String(colName || "").toLowerCase().trim();

  // no coloring on raw count columns (Win / T3 / T5 / T10)
  if (/^(win|t3|t5|t10)$/.test(k)) return null;

  // Lower-better
  if (/^proj\s*fin$/.test(k)) return "lower";
  if (/fair\s*odds/.test(k)) return "lower";

  // Higher-better
  if (/win\s*%/.test(k)) return "higher";
  if (/t(?:3|5|10|20)\s*%/.test(k)) return "higher";
  if (/^proj(?!.*fin)/.test(k)) return "higher";

  // Finish Dist P# columns (P1..)
  if (/^p\d+$/.test(k)) return "higher";

  return null;
};

/**
 * Theme-aware heatmap (same as projections):
 *  - rdylgn: red → yellow → green (green = better)
 *  - blueorange: blue → white → orange (orange = better)
 */
function heatTextColor(min, max, v, dir, theme = "dark") {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (!Number.isFinite(v) || min === max || !dir) return null;

  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  const isDark = theme === "dark";

  // orange/red -> yellow -> green
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  } else {
    const u = (t - 0.5) / 0.5;
    const h = 50 + u * 70;
    const s = isDark ? 88 : 80;
    const l = isDark ? 62 + u * 8 : 44 - u * 4;
    return `hsl(${h}, ${s}%, ${l}%)`;
  }
}

/* ------------------------ Reusable Table Block (heatmap) ------------------------ */
/* NOTE: this is used for Driver Sims only – no compact toggle, no always-on chips */
function BettingTableBlock({
  title,
  source,
  maxHeight = "70vh",
  theme = "dark",
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
  labelCol = "Driver",
  showSearch = true,
  showExport = true,
  showRowCount = true,
}) {
  const SHOW_SOURCE = false;
  const { data, err, loading } = useJson(source);

  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }, [data]);

  const allCols = useMemo(() => (rows.length ? Object.keys(rows[0]) : []), [rows]);

  // we just show all columns; no user chips
  const visibleColNames = allCols;

  // search
  const [q, setQ] = useState("");
  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      allCols.some((c) => String(r?.[c] ?? "").toLowerCase().includes(needle))
    );
  }, [rows, allCols, q]);

  // heat stats (only visible cols & filtered rows)
  const heatStats = useMemo(() => {
    const stats = {};
    if (!filteredRows.length) return stats;
    const cols = visibleColNames.length ? visibleColNames : allCols;

    for (const c of cols) {
      const dir = dirForCol(c);
      if (!dir) continue;
      let min = Infinity,
        max = -Infinity;
      for (const r of filteredRows) {
        const n = num(r?.[c]);
        if (Number.isFinite(n)) {
          if (n < min) min = n;
          if (n > max) max = n;
        }
      }
      if (min !== Infinity && max !== -Infinity) stats[c] = { min, max, dir };
    }
    return stats;
  }, [filteredRows, visibleColNames, allCols]);

  // sort
  const [sort, setSort] = useState({ col: "", dir: "asc" });
  const sortedRows = useMemo(() => {
    if (!sort.col) return filteredRows;
    return [...filteredRows].sort((a, b) => cmp(a?.[sort.col], b?.[sort.col], sort.dir, sort.col));
  }, [filteredRows, sort]);

  // auto-fit widths
  const colWidthCh = useMemo(() => {
    const widths = {};
    if (!rows.length) return widths;
    const sample = rows.slice(0, 200);
    for (const c of allCols) {
      let maxLen = 0;
      const headerLen = String(c).length;
      for (const r of sample) {
        const s = String(r?.[c] ?? "");
        if (s.length > maxLen) maxLen = s.length;
      }
      if (isDriverCol(c)) widths[c] = clamp(Math.max(maxLen, 12), 12, 22);
      else if (looksNumericCol(c)) widths[c] = clamp(Math.max(maxLen + 2, 6), 6, 12);
      else widths[c] = clamp(Math.max(maxLen, Math.min(headerLen, 12)), 10, 16);
    }
    return widths;
  }, [rows, allCols]);

  const resetUI = () => {
    setQ("");
    setSort({ col: "", dir: "asc" });
  };

  const exportCSV = () => {
    const cols = visibleColNames.length ? visibleColNames : allCols;
    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of sortedRows)
      lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/\s+/g, "_").toLowerCase()}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const densityRow = "px-2 py-1 text-xs";
  const densityHead = "px-2 py-1 text-xs";

  const colsInUse = visibleColNames.length ? visibleColNames : allCols;

  const hoverRowClass = isDark ? "hover:bg-sky-900/40" : "hover:bg-blue-50/60";

  const cellBorder = isDark ? "border-b border-slate-800/80" : "border-b border-gray-100";

  // IMPORTANT: make sticky driver column & sticky header FULLY OPAQUE
  // and give sticky driver col a proper shadow, so it never looks "transparent".
  const stickyDriverShadow = isDark
    ? "shadow-[6px_0_12px_rgba(0,0,0,0.25)]"
    : "shadow-[6px_0_12px_rgba(15,23,42,0.12)]";

  const stickyBorder = isDark ? "border-r border-slate-800/80" : "border-r border-gray-200";

  return (
    <section className={cardClass + " mb-5"}>
      <div className={"flex flex-col gap-3 border-b px-4 py-3 lg:flex-row lg:items-center lg:gap-4 " + headerBorderClass}>
        <h2 className="text-sm font-semibold">{title}</h2>
        {SHOW_SOURCE && <code className="text-sm text-gray-500">{source}</code>}
        <div className="flex w-full flex-wrap items-center gap-2 lg:ml-auto lg:w-auto">
          {showSearch && (
            <>
              <input
                type="search"
                aria-label="Search driver simulations"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className={
                  "h-8 w-full min-w-0 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 sm:w-64 " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                    : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
                }
              />
              <button
                className={
                  "h-8 rounded-md border px-3 text-xs " +
                  (isDark
                    ? "bg-slate-900/80 text-slate-100 hover:bg-slate-800 border-slate-700/80"
                    : "bg-white text-slate-900 hover:bg-gray-50 border-gray-300")
                }
                onClick={resetUI}
              >
                Reset
              </button>
            </>
          )}

          {showExport && (
            <button
              className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 shadow-sm hover:bg-sky-400"
              onClick={exportCSV}
            >
              Export CSV
            </button>
          )}


        </div>
      </div>

      {loading && (
        <div className={isDark ? "text-sm text-slate-400" : "text-sm text-gray-600"}>Loading…</div>
      )}
      {err && (
        <div className={isDark ? "text-sm text-red-400" : "text-sm text-red-600"}>
          Failed to load: {String(err)}
        </div>
      )}

      {!loading && !err && (
        <div className="overflow-auto rounded-b-2xl" style={{ maxHeight }}>
          <table className="min-w-full w-max table-auto border-separate text-xs" style={{ borderSpacing: 0 }}>
            <colgroup>
              {colsInUse.map((c) => (
                <col key={c} style={{ width: `${colWidthCh[c] ?? 10}ch` }} />
              ))}
            </colgroup>

            <thead>
              <tr>
                {colsInUse.map((c) => {
                  const active = sort.col === c;
                  const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "▲";
                  const w = colWidthCh[c] ?? 10;

                  return (
                    <th
                      key={c}
                      onClick={() =>
                        setSort((s) =>
                          s.col === c
                            ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                            : { col: c, dir: "asc" }
                        )
                      }
                      title={c}
                      className={[
                        "sticky top-0",
                        // make header fully opaque always
                        headerBgClass,
                        "font-semibold border-b",
                        headerBorderClass,
                        densityHead,
                        "cursor-pointer select-none text-center",
                        "overflow-hidden",
                        // ✅ FIX: reduce z-index so it never beats mobile menu overlays
                        isDriverCol(c) ? "left-0 z-20 " + stickyDriverShadow : "z-10",
                      ].join(" ")}
                      style={{ maxWidth: `${w}ch` }}
                    >
                      <div className="inline-flex items-center gap-1 justify-center w-full whitespace-normal break-words leading-tight">
                        {c} <span className="opacity-60 text-[10px]">{arrow}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sortedRows.map((r, i) => {
                const rowBg = i % 2 ? rowEvenClass : rowOddClass;

                return (
                  <tr key={i} className={[rowBg, hoverRowClass, "transition-colors"].join(" ")}>
                    {colsInUse.map((c) => {
                      const stat = heatStats[c];
                      const textColor = stat
                        ? heatTextColor(stat.min, stat.max, num(r?.[c]), stat.dir, theme)
                        : null;

                      const isDriver = isDriverCol(c);

                      return (
                        <td
                          key={c}
                          className={[
                            cellBorder,
                            densityRow,
                            "text-center tabular-nums",
                            isDriver
                              ? [
                                  "sticky left-0 z-10",
                                  rowBg,
                                  stickyBorder,
                                  stickyDriverShadow,
                                  "text-left",
                                ].join(" ")
                              : "",
                          ].join(" ")}
                          style={{
                            maxWidth: `${colWidthCh[c] ?? 10}ch`,
                          }}
                          title={String(r?.[c] ?? "")}
                        >
                          <div className="w-full whitespace-normal break-words">
                            {isDriver ? (
                              r?.[c] ?? ""
                            ) : (
                              <span
                                style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                              >
                                {r?.[c] ?? ""}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {!sortedRows.length && (
                <tr>
                  <td
                    className={"px-2 py-1 text-xs " + (isDark ? "text-slate-500" : "text-gray-500")}
                    colSpan={colsInUse.length}
                  >
                    No rows.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ------------- Manufacturer Sims (clean exports + fallback) ------------- */
/* ------------- Manufacturer Sims (clean exports + fallback) ------------- */
function ManufacturerSections({
  series,
  mfgWinsSource,
  fordSource,
  chevySource,
  toyotaSource,
  RAMSource,        // ✅ NEW
  fallbackSource,
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
}) {
  const isTrucks = String(series || "").toLowerCase() === "trucks";

  const { data: mfgWinsRaw, err: mfgWinsErr } = useJson(mfgWinsSource);

  const { data: fordRaw, err: fordErr } = useJson(fordSource);
  const { data: chevyRaw, err: chevyErr } = useJson(chevySource);
  const { data: toyotaRaw, err: toyotaErr } = useJson(toyotaSource);

  // ✅ Only fetch Dodge when Trucks (prevents 404s on Cup/O'Reilly)
  const { data: RAMRaw, err: RAMErr } = useJson(isTrucks ? RAMSource : null);

  // optional wide fallback (if you ever combine exports into one file)
  const { data: fallbackRaw } = useJson(fallbackSource);

  const mfgWins = useMemo(() => {
    const raw = Array.isArray(mfgWinsRaw) && !mfgWinsErr ? mfgWinsRaw : fallbackRaw;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((r) => ({
        Manufacturer: String(r?.Manufacturer ?? "").trim(),
        Proj: toPct(r?.Proj),
        FairOdds: asInt(r?.["Fair Odds"]),
      }))
      .filter((r) => r.Manufacturer)
      .map((r) => ({ ...r, FairOdds: r.FairOdds === null ? "" : r.FairOdds }));
  }, [mfgWinsRaw, mfgWinsErr, fallbackRaw]);

  const parseTop = useCallback((raw, driverKey, projKey, oddsKey) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((r) => ({
        Driver: String(r?.[driverKey] ?? "").trim(),
        Proj: toPct(r?.[projKey]),
        FairOdds: asInt(r?.[oddsKey]),
      }))
      .filter((x) => x.Driver)
      .map((x) => ({ ...x, FairOdds: x.FairOdds === null ? "" : x.FairOdds }));
  }, []);

  const wide = Array.isArray(fallbackRaw) ? fallbackRaw : [];
  const wideHas = (k) => wide.some((r) => Object.prototype.hasOwnProperty.call(r || {}, k));

  const topFord = useMemo(
    () =>
      Array.isArray(fordRaw) && !fordErr
        ? parseTop(fordRaw, "Ford Drivers", "Proj_1", "Fair Odds_1")
        : wideHas("Proj_1")
        ? parseTop(wide, "Ford Drivers", "Proj_1", "Fair Odds_1")
        : [],
    [fordRaw, fordErr, parseTop, wide]
  );

  const topChevy = useMemo(
    () =>
      Array.isArray(chevyRaw) && !chevyErr
        ? parseTop(chevyRaw, "Chevrolet Drivers", "Proj_2", "Fair Odds_2")
        : wideHas("Proj_2")
        ? parseTop(wide, "Chevrolet Drivers", "Proj_2", "Fair Odds_2")
        : [],
    [chevyRaw, chevyErr, parseTop, wide]
  );

  const topToyota = useMemo(
    () =>
      Array.isArray(toyotaRaw) && !toyotaErr
        ? parseTop(toyotaRaw, "Toyota Drivers", "Proj_3", "Fair Odds_3")
        : wideHas("Proj_3")
        ? parseTop(wide, "Toyota Drivers", "Proj_3", "Fair Odds_3")
        : [],
    [toyotaRaw, toyotaErr, parseTop, wide]
  );

  // ✅ Dodge export is its own file, so its keys are NOT _4
  // It will look like: "Dodge Drivers", "Proj", "Fair Odds"
  // ✅ Dodge: support both old wide keys (_4) and clean keys (no suffix)
  const topDodge = useMemo(() => {
    if (!isTrucks || !Array.isArray(RAMRaw) || RAMErr) return [];

    // try clean export first
    const hasClean = RAMRaw.some(
      (r) => r && ("Proj" in r || "Fair Odds" in r)
    );

    if (hasClean) {
      return parseTop(RAMRaw, "RAM Drivers", "Proj", "Fair Odds");
    }

    // fallback to wide-style export keys
    return parseTop(RAMRaw, "RAM Drivers", "Proj_4", "Fair Odds_4");
  }, [isTrucks, RAMRaw, RAMErr, parseTop]);

  return (
    <div className={"grid gap-4 md:grid-cols-2 " + (isTrucks ? "" : "xl:grid-cols-3")}>
      <MiniTable
        title="Top Ford Driver"
        rows={topFord}
        showSearch={false}
        defaultSortCol="FairOdds"
        labelCol="Driver"
        cardClass={cardClass}
        headerBgClass={headerBgClass}
        headerBorderClass={headerBorderClass}
        rowOddClass={rowOddClass}
        rowEvenClass={rowEvenClass}
        isDark={isDark}
      />
      <MiniTable
        title="Top Chevrolet Driver"
        rows={topChevy}
        showSearch={false}
        defaultSortCol="FairOdds"
        labelCol="Driver"
        cardClass={cardClass}
        headerBgClass={headerBgClass}
        headerBorderClass={headerBorderClass}
        rowOddClass={rowOddClass}
        rowEvenClass={rowEvenClass}
        isDark={isDark}
      />
      <MiniTable
        title="Top Toyota Driver"
        rows={topToyota}
        showSearch={false}
        defaultSortCol="FairOdds"
        labelCol="Driver"
        cardClass={cardClass}
        headerBgClass={headerBgClass}
        headerBorderClass={headerBorderClass}
        rowOddClass={rowOddClass}
        rowEvenClass={rowEvenClass}
        isDark={isDark}
      />

      {isTrucks && (
        <MiniTable
          title="Top RAM Driver"
          rows={topDodge}
          showSearch={false}
          defaultSortCol="FairOdds"
          labelCol="Driver"
          cardClass={cardClass}
          headerBgClass={headerBgClass}
          headerBorderClass={headerBorderClass}
          rowOddClass={rowOddClass}
          rowEvenClass={rowEvenClass}
          isDark={isDark}
        />
      )}
    </div>
  );
}
function TeamSections({
  teamSource,
  mfgWinsSource,
  fallbackSource,
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
}) {
  const { data: teamRaw, err: teamErr } = useJson(teamSource);
  const { data: mfgWinsRaw, err: mfgWinsErr } = useJson(mfgWinsSource);
  const { data: fallbackRaw } = useJson(fallbackSource);


const teams = useMemo(() => {
  const raw = Array.isArray(teamRaw) && !teamErr ? teamRaw : fallbackRaw;
  if (!Array.isArray(raw)) return [];

  const has5 = raw.some((r) => r && ("Proj_5" in r || "Fair Odds_5" in r));
  const has4 = raw.some((r) => r && ("Proj_4" in r || "Fair Odds_4" in r));

  const base = has5
    ? raw.map((r) => ({
        Team: (r?.Team ?? "").toString().trim(),
        ProjRaw: r?.Proj_5,
        FairOddsRaw: r?.["Fair Odds_5"],
      }))
    : has4
    ? raw.map((r) => ({
        Team: (r?.Team ?? "").toString().trim(),
        ProjRaw: r?.Proj_4,
        FairOddsRaw: r?.["Fair Odds_4"],
      }))
    : raw.map((r) => ({
        Team: (r?.Team ?? "").toString().trim(),
        ProjRaw: r?.Proj,
        FairOddsRaw: r?.["Fair Odds"],
      }));

  return base
    .map((r) => {
      const projNum = pctNumber(r?.ProjRaw);
      const fair = asInt(r?.FairOddsRaw);
      return {
        Team: r.Team,
        Proj: toPct(r?.ProjRaw),
        ProjNum: Number.isFinite(projNum) ? projNum : -Infinity,
        FairOdds: fair === null ? "" : fair,
      };
    })
    .filter((r) => r.Team)
    .sort((a, b) => (b.ProjNum ?? -Infinity) - (a.ProjNum ?? -Infinity))
    .map(({ ProjNum, ...rest }) => rest);
}, [teamRaw, teamErr, fallbackRaw]);


const mfgWins = useMemo(() => {
  if (!Array.isArray(mfgWinsRaw) || mfgWinsErr) return [];
  return mfgWinsRaw
    .map((r) => {
      const projNum = pctNumber(r?.Proj);
      const fair = asInt(r?.["Fair Odds"]);
      return {
        Manufacturer: (r?.Manufacturer ?? "").toString().trim(),
        Proj: toPct(r?.Proj),
        ProjNum: Number.isFinite(projNum) ? projNum : -Infinity,
        FairOdds: fair === null ? "" : formatOdds(fair),
      };
    })
    .filter((r) => r.Manufacturer)
    .sort((a, b) => (b.ProjNum ?? -Infinity) - (a.ProjNum ?? -Infinity))
    .map(({ ProjNum, ...rest }) => rest);
}, [mfgWinsRaw, mfgWinsErr]);


const exportRowsCSV = useCallback((label, rows, cols) => {
  if (!Array.isArray(rows) || !rows.length) return;
  const safeCols = Array.isArray(cols) && cols.length ? cols : Object.keys(rows[0] || {});
  const lines = [];
  lines.push(safeCols.map(escapeCSV).join(","));
  for (const r of rows) lines.push(safeCols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));

  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${label.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}, []);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <MiniTable
        title="Winning Manufacturer"
        rows={mfgWins}
        showSearch={false}
        defaultSortCol="Proj"
        defaultSortDir="desc"
        cardClass={cardClass}
        headerBgClass={headerBgClass}
        headerBorderClass={headerBorderClass}
        rowOddClass={rowOddClass}
        rowEvenClass={rowEvenClass}
        isDark={isDark}
        labelCol="Manufacturer"
      />

      <div className="md:col-span-2">
        <MiniTable
          title="Winning Team"
          rows={teams}
          showSearch={false}
          defaultSortCol="Proj"
          defaultSortDir="desc"
          cardClass={cardClass}
          headerBgClass={headerBgClass}
          headerBorderClass={headerBorderClass}
          rowOddClass={rowOddClass}
          rowEvenClass={rowEvenClass}
          isDark={isDark}
          labelCol="Team"
        />
      </div>
    </div>
  );
}


function MiniTable({
  title,
  rows,
  showSearch = true,
  defaultSortCol = "FairOdds",
  defaultSortDir = "asc",
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
  labelCol = "Driver",
}) {
  const cols = [labelCol, "Proj", "FairOdds"];

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!showSearch) return Array.isArray(rows) ? rows : [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      cols.some((c) => String(r?.[c] ?? "").toLowerCase().includes(s))
    );
  }, [rows, q, showSearch]);

  const [sort, setSort] = useState({ col: defaultSortCol, dir: defaultSortDir });
  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const pa = num(a?.Proj) ?? 0;
        const pb = num(b?.Proj) ?? 0;
        return pb - pa; // DESC (highest first)
      }),
    [filtered]
  );

  const exportCSV = () => {
    const lines = [];
    lines.push(cols.join(","));
    for (const r of sorted) lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${title.toLowerCase().replace(/\s+/g, "_")}_sims_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const widths = useMemo(() => {
    const w = {};
    const sample = rows.slice(0, 100);
    for (const c of cols) {
      let maxLen = c.length;
      for (const r of sample) maxLen = Math.max(maxLen, String(r?.[c] ?? "").length);
      w[c] =
        c === labelCol
          ? clamp(Math.max(maxLen, 12), 12, 22)
          : clamp(Math.max(maxLen + 2, 6), 6, 12);
    }
    return w;
  }, [rows]);

  const densityRow = "px-2 py-1 text-xs";
  const densityHead = "px-2 py-1 text-xs";

  const hoverRowClass = isDark ? "hover:bg-sky-900/40" : "hover:bg-blue-50/60";
  const cellBorder = isDark ? "border-b border-slate-800/80" : "border-b border-gray-100";

  return (
    <div className={cardClass + " overflow-auto p-3"}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold">{title}</div>
        {showSearch && (
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search this section…"
            className={
              "ml-auto h-8 w-40 sm:w-44 rounded-md border px-2 text-sm focus:ring-2 focus:ring-sky-500 " +
              (isDark
                ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
            }
          />
        )}
        <button
          className={"h-8 px-3 text-xs rounded-md bg-sky-500 text-slate-950 font-semibold hover:bg-sky-400 shadow-sm " + (showSearch ? "" : "ml-auto")}
          onClick={exportCSV}
        >
          Export
        </button>
      </div>

      <table className="w-full table-fixed border-separate" style={{ borderSpacing: 0 }}>
        <colgroup>
          {cols.map((c) => (
            <col key={c} style={{ width: `${widths[c] ?? 10}ch` }} />
          ))}
        </colgroup>
        <thead>
          {/* ✅ FIX: reduce z-index so it never beats mobile menu overlays */}
          <tr className="sticky top-0 z-10">
            {cols.map((c) => {
              const active = sort.col === c;
              const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "▲";
              return (
                <th
                  key={c}
                  className={[
                    headerBgClass, // keep opaque by design in parent
                    "font-semibold border-b",
                    headerBorderClass,
                    "text-center cursor-pointer select-none",
                    densityHead,
                  ].join(" ")}
                  onClick={() =>
                    setSort((s) =>
                      s.col === c
                        ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                        : { col: c, dir: "asc" }
                    )
                  }
                  title={c}
                >
                  <span className="inline-flex items-center gap-1">
                    {c} <span className="opacity-60 text-[10px]">{arrow}</span>
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const rowBg = i % 2 ? rowEvenClass : rowOddClass;
            return (
              <tr key={i} className={[rowBg, hoverRowClass].join(" ")}>
                {cols.map((c) => (
                  <td
                    key={c}
                    className={[
                      cellBorder,
                      "tabular-nums",
                      c === labelCol ? "text-left" : "text-center",
                      densityRow,
                    ].join(" ")}
                    title={String(r?.[c] ?? "")}
                  >
                    {c === "FairOdds" ? displayFairOdds(r?.[c]) : (r?.[c] ?? "")}
                  </td>
                ))}
              </tr>
            );
          })}
          {!sorted.length && (
            <tr>
              <td
                className={"px-2 py-2 text-xs " + (isDark ? "text-slate-500" : "text-gray-500")}
                colSpan={cols.length}
              >
                No rows.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


/* ------------- Team Sims (Winning Team) Mini Table (proj-desc, blanks-bottom, compact) ------------- */
function TeamMiniTable({
  title,
  rows,
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
}) {
  const cols = ["Team", "Proj", "FairOdds"];

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      cols.some((c) => String(r?.[c] ?? "").toLowerCase().includes(s))
    );
  }, [rows, q, cols, showSearch]);

  // default: highest win proj -> lowest
  const [sort, setSort] = useState({ col: "Proj", dir: "desc" });

  const cmpTeam = (a, b, col, dir) => {
    const isNumCol = col === "Proj" || col === "FairOdds";
    if (isNumCol) {
      const na = num(a);
      const nb = num(b);
      const aGood = Number.isFinite(na);
      const bGood = Number.isFinite(nb);

      // blanks/non-numeric always bottom
      if (aGood && !bGood) return -1;
      if (!aGood && bGood) return 1;
      if (!aGood && !bGood) return 0;

      return dir === "asc" ? na - nb : nb - na;
    }

    const sa = String(a ?? "").toLowerCase();
    const sb = String(b ?? "").toLowerCase();
    if (sa < sb) return dir === "asc" ? -1 : 1;
    if (sa > sb) return dir === "asc" ? 1 : -1;
    return 0;
  };

  const sorted = useMemo(() => {
    return [...filtered].sort((ra, rb) =>
      cmpTeam(ra?.[sort.col], rb?.[sort.col], sort.col, sort.dir)
    );
  }, [filtered, sort]);

  const exportCSV = () => {
    const lines = [];
    lines.push(cols.join(","));
    for (const r of sorted) lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `team_sims_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // compact sizing (Team only)
  const densityRow = "px-2 py-1 text-xs";
  const densityHead = "px-2 py-1 text-xs";
  const hoverRowClass = isDark ? "hover:bg-sky-900/40" : "hover:bg-blue-50/60";
  const cellBorder = isDark ? "border-b border-slate-800/80" : "border-b border-gray-100";

  const widths = useMemo(() => {
    const w = {};
    const sample = (rows || []).slice(0, 120);
    for (const c of cols) {
      let maxLen = c.length;
      for (const r of sample) maxLen = Math.max(maxLen, String(r?.[c] ?? "").length);
      if (c === "Team") w[c] = clamp(Math.max(maxLen, 14), 14, 26);
      else w[c] = clamp(Math.max(maxLen + 2, 6), 6, 10);
    }
    return w;
  }, [rows]);

  return (
    <div className={cardClass + " overflow-auto p-3 w-fit max-w-full"}>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-sm font-semibold">{title}</div>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search this section…"
          className={
            "ml-auto h-8 w-40 sm:w-44 rounded-md border px-2 text-sm focus:ring-2 focus:ring-sky-500 " +
            (isDark
              ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
              : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
          }
        />

        <button
          className="h-8 px-2 text-xs rounded-md bg-sky-500 text-slate-950 font-semibold hover:bg-sky-400"
          onClick={exportCSV}
        >
          Export
        </button>
      </div>

      <table className="w-fit table-fixed border-separate" style={{ borderSpacing: 0 }}>
        <colgroup>
          {cols.map((c) => (
            <col key={c} style={{ width: `${widths[c] ?? 10}ch` }} />
          ))}
        </colgroup>
        <thead>
          <tr className="sticky top-0 z-10">
            {cols.map((c) => {
              const active = sort.col === c;
              const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "▲";
              return (
                <th
                  key={c}
                  className={[
                    headerBgClass,
                    "font-semibold border-b",
                    headerBorderClass,
                    "text-center cursor-pointer select-none",
                    densityHead,
                  ].join(" ")}
                  onClick={() =>
                    setSort((s) =>
                      s.col === c
                        ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                        : { col: c, dir: c === "Proj" ? "desc" : c === "FairOdds" ? "asc" : "asc" }
                    )
                  }
                  title={c}
                >
                  <span className="inline-flex items-center gap-1">
                    {c} <span className="opacity-60 text-[10px]">{arrow}</span>
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const rowBg = i % 2 ? rowEvenClass : rowOddClass;
            return (
              <tr key={i} className={[rowBg, hoverRowClass].join(" ")}>
                {cols.map((c) => (
                  <td
                    key={c}
                    className={[
                      cellBorder,
                      densityRow,
                      "tabular-nums",
                      c === "Team" ? "text-left" : "text-center",
                    ].join(" ")}
                    title={String(r?.[c] ?? "")}
                  >
                    {c === "FairOdds" ? displayFairOdds(r?.[c]) : (r?.[c] ?? "")}
                  </td>
                ))}
              </tr>
            );
          })}
          {!sorted.length && (
            <tr>
              <td
                className={"px-2 py-2 text-xs " + (isDark ? "text-slate-500" : "text-gray-500")}
                colSpan={cols.length}
              >
                No rows.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


/* ----------------------------- Driver H2H Tool (sleek, mobile-first, fixed zoom) ----------------------------- */
function H2HMatrixTool({
  source,
  maxHeight = "78vh",
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
}) {
  const SHOW_SOURCE = false;
  const { data, err, loading } = useJson(source);

  // normalize rows
  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }, [data]);

  const drivers = useMemo(() => {
    if (!rows.length) return [];
    const names = new Set();
    for (const r of rows) if (r?.Driver) names.add(String(r.Driver));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // build grid lookup
  const grid = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const d = String(r?.Driver ?? "");
      if (!d) continue;
      g[d] = g[d] || {};
      for (const [k, v] of Object.entries(r)) {
        if (k === "Driver") continue;
        const n = Number(String(v ?? "").toString().replace(/[,%\s]/g, ""));
        if (Number.isFinite(n)) g[d][k] = n;
      }
    }
    return g;
  }, [rows]);

  // selections
  const [A, setA] = useState("");
  const [B, setB] = useState("");
  useEffect(() => {
    if (!A && drivers.length) setA(drivers[0]);
    if (!B && drivers.length > 1) setB(drivers[1]);
  }, [drivers, A, B]);

  const getAB = useCallback(
    (a, b) => {
      if (!a || !b || a === b) return NaN;
      const direct = grid?.[a]?.[b];
      if (Number.isFinite(direct)) return direct;
      const back = grid?.[b]?.[a];
      if (Number.isFinite(back)) return 100 - back;
      return NaN;
    },
    [grid]
  );

  const probA = getAB(A, B);
  const probB = Number.isFinite(probA) ? 100 - probA : NaN;

  const toAmerican = (p) => {
    const q = p / 100;
    if (!(q > 0 && q < 1)) return "—";
    if (q >= 0.5) return Math.round((-100 * q) / (1 - q)).toString();
    return `+${Math.round((100 * (1 - q)) / q)}`;
  };

  const fmt1pct = (n) => (Number.isFinite(n) ? `${n.toFixed(1)}%` : "—");
  const fmtSignedPct = (n) =>
    Number.isFinite(n) ? `${n >= 0 ? "+" : ""}${n.toFixed(1)}%` : "—";

  const exportCSV = () => {
    if (!rows.length) return;
    const cols = ["Driver", ...drivers];
    const header = cols.map(escapeCSV).join(",");
    const lines = [header];
    for (const d of drivers) {
      const r = grid[d] || {};
      const line = [d, ...drivers.map((op) => r?.[op] ?? (op === d ? "" : ""))]
        .map(escapeCSV)
        .join(",");
      lines.push(line);
    }
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `driver_h2h_matrix_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // ✅ Fixed zoom (no slider). Always 90%.
  const zoom = 0.9;
  const baseCol = 76; // px
  const colW = Math.round(baseCol * zoom);
  const fontPx = Math.round(12 * zoom);

  const hoverRowClass = isDark ? "hover:bg-sky-900/40" : "hover:bg-blue-50/60";

  const stickyShadow = isDark
    ? "shadow-[6px_0_12px_rgba(0,0,0,0.25)]"
    : "shadow-[6px_0_12px_rgba(15,23,42,0.12)]";

  // controls
  const [filter, setFilter] = useState("");
  const filteredDrivers = useMemo(() => {
    const s = filter.trim().toLowerCase();
    if (!s) return drivers;
    return drivers.filter((d) => d.toLowerCase().includes(s));
  }, [drivers, filter]);

  // Keep A/B valid even if filter hides them
  useEffect(() => {
    if (!drivers.length) return;
    if (A && drivers.includes(A) === false) setA(drivers[0]);
    if (B && drivers.includes(B) === false) setB(drivers[1] || drivers[0]);
  }, [drivers]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickFromVisible = useCallback(() => {
    const pool = filteredDrivers.length ? filteredDrivers : drivers;
    if (pool.length < 2) return;
    let i = Math.floor(Math.random() * pool.length);
    let j = Math.floor(Math.random() * pool.length);
    if (i === j) j = (j + 1) % pool.length;
    setA(pool[i]);
    setB(pool[j]);
  }, [filteredDrivers, drivers]);

  // Simple “matchup bar” for a slick at-a-glance read
  const barA = Number.isFinite(probA) ? clamp(probA, 0, 100) : 0;
  const barB = Number.isFinite(probB) ? clamp(probB, 0, 100) : 0;

  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-sm font-semibold">Driver H2H Tool</h2>
        {SHOW_SOURCE && (
          <code className="text-[11px] ml-2 px-2 py-1 rounded bg-gray-50 border text-gray-600">
            {source}
          </code>
        )}
        {loading && (
          <span className={"text-xs ml-2 " + (isDark ? "text-slate-400" : "text-gray-500")}>
            loading…
          </span>
        )}
        {err && (
          <span className={"text-xs ml-2 " + (isDark ? "text-red-400" : "text-red-600")}>
            error: {String(err)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={pickFromVisible}
            className={
              "h-8 px-3 text-xs rounded-md border " +
              (isDark
                ? "bg-slate-900/80 text-slate-100 hover:bg-slate-800 border-slate-700/80"
                : "bg-white text-slate-900 hover:bg-gray-50 border-gray-300")
            }
            title="Random matchup"
          >
            Random
          </button>

          <button
            onClick={exportCSV}
            className="h-8 px-3 text-xs rounded-md bg-sky-500 text-slate-950 font-semibold hover:bg-sky-400 shadow-sm"
          >
            Export Matrix CSV
          </button>
        </div>
      </div>

      {/* Picker + results */}
      {!loading && !err && !!drivers.length && (
        <div className={cardClass + " p-4 mb-4"}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold mb-1">Driver A</label>
              <select
                value={A}
                onChange={(e) => setA(e.target.value)}
                className={
                  "w-full border rounded-md px-3 py-2 text-sm " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100"
                    : "bg-white border-slate-300 text-slate-900")
                }
              >
                {drivers.map((d) => (
                  <option key={`A-${d}`} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Driver B</label>
              <select
                value={B}
                onChange={(e) => setB(e.target.value)}
                className={
                  "w-full border rounded-md px-3 py-2 text-sm " +
                  (isDark
                    ? "bg-slate-950/80 border-slate-700 text-slate-100"
                    : "bg-white border-slate-300 text-slate-900")
                }
              >
                {drivers.map((d) => (
                  <option key={`B-${d}`} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  const a = A,
                    b = B;
                  setA(b);
                  setB(a);
                }}
                className={
                  "w-full sm:w-auto mt-0 sm:mt-5 h-8 px-3 text-xs rounded-md border " +
                  (isDark
                    ? "bg-slate-900/80 text-slate-100 hover:bg-slate-800 border-slate-700/80"
                    : "bg-white text-slate-900 hover:bg-gray-50 border-gray-300")
                }
              >
                Swap
              </button>
            </div>
          </div>

          {/* Matchup header + bar */}
          <div className="mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-600")}>Matchup</div>
              <div className="text-lg sm:text-xl font-extrabold tracking-tight">
                {A || "—"} <span className={isDark ? "text-slate-500" : "text-slate-400"}>vs</span>{" "}
                {B || "—"}
              </div>
              <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
                <span
                  className={
                    "text-xs px-2 py-1 rounded-full border " +
                    (isDark ? "border-slate-700/70 bg-slate-950/60 text-slate-200" : "border-slate-200 bg-white text-slate-800")
                  }
                  title="Fair odds for Driver A"
                >
                  A: {toAmerican(probA)}
                </span>
                <span
                  className={
                    "text-xs px-2 py-1 rounded-full border " +
                    (isDark ? "border-slate-700/70 bg-slate-950/60 text-slate-200" : "border-slate-200 bg-white text-slate-800")
                  }
                  title="Fair odds for Driver B"
                >
                  B: {toAmerican(probB)}
                </span>
                <span
                  className={
                    "text-xs px-2 py-1 rounded-full border " +
                    (isDark ? "border-slate-700/70 bg-slate-950/60 text-slate-200" : "border-slate-200 bg-white text-slate-800")
                  }
                  title="Difference from 50/50"
                >
                  vs 50/50:{" "}
                  {Number.isFinite(probA) ? fmtSignedPct(probA - 50) : "—"}
                </span>
              </div>
            </div>

            <div
              className={
                "mt-3 rounded-full overflow-hidden border " +
                (isDark ? "border-slate-800 bg-slate-950/70" : "border-slate-200 bg-white")
              }
              style={{ height: 12 }}
              aria-hidden="true"
            >
              <div
                className="h-full"
                style={{
                  width: `${barA}%`,
                  background: isDark
                    ? "linear-gradient(90deg, rgba(56,189,248,0.25), rgba(56,189,248,0.75))"
                    : "linear-gradient(90deg, rgba(59,130,246,0.20), rgba(59,130,246,0.70))",
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {[
                { prob: probA, d1: A, d2: B },
                { prob: probB, d1: B, d2: A },
              ].map((blk, idx) => (
                <div
                  key={idx}
                  className={
                    "rounded-2xl border p-4 " +
                    (isDark ? "border-slate-800 bg-slate-900/80" : "border-slate-200 bg-white")
                  }
                >
                  <div className={"text-sm " + (isDark ? "text-slate-400" : "text-gray-600")}>
                    Chance {blk.d1 || "—"} beats {blk.d2 || "—"}
                  </div>
                  <div className="mt-1 flex items-end gap-3">
                    <div className="text-3xl font-extrabold leading-none">{fmt1pct(blk.prob)}</div>
                    <div className={"text-xs mb-0.5 " + (isDark ? "text-slate-400" : "text-gray-600")}>
                      Fair odds: {toAmerican(blk.prob)}
                    </div>
                  </div>
                  <div className={"text-xs mt-2 " + (isDark ? "text-slate-400" : "text-gray-600")}>
                    vs 50/50:{" "}
                    {Number.isFinite(blk.prob) ? fmtSignedPct(blk.prob - 50) : "—"}
                  </div>
                </div>
              ))}
            </div>

            <div className={"mt-3 text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
              Tip: tap a cell to set Driver A/B. (Zoom is fixed at 90% for readability.)
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className={isDark ? "text-sm text-slate-400" : "text-sm text-gray-600"}>Loading…</div>
      )}
      {err && (
        <div className={isDark ? "text-sm text-red-400" : "text-sm text-red-600"}>
          Failed to load: {String(err)}
        </div>
      )}

      {/* Matrix controls + heatmap table */}
      {!loading && !err && !!drivers.length && (
        <>
          <div className={cardClass + " p-3 mb-3"}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="text-sm font-semibold">Matrix</div>
              <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-600")}>
                {filteredDrivers.length.toLocaleString()} / {drivers.length.toLocaleString()} drivers
              </div>
              <div className="sm:ml-auto w-full sm:w-auto">
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter drivers…"
                  className={
                    "w-full sm:w-72 border rounded-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                    (isDark
                      ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                      : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
                  }
                />
              </div>
            </div>
          </div>

          <div className={cardClass + " overflow-auto"} style={{ maxHeight }}>
            <table
              className="w-full border-separate"
              style={{
                borderSpacing: 0,
                fontSize: `${fontPx}px`,
                lineHeight: 1.15,
                // min-width based on filtered list so it doesn't explode on mobile
                minWidth: `${Math.max(520, filteredDrivers.length * (colW + 2))}px`,
              }}
            >
              <colgroup>
                <col style={{ width: `${Math.max(160, 140 * zoom)}px` }} />
                {filteredDrivers.map((d) => (
                  <col key={`colw-${d}`} style={{ width: `${colW}px` }} />
                ))}
              </colgroup>

              {/* ✅ FIX: reduce z-index so it never beats mobile menu overlays */}
              <thead className="sticky top-0 z-10">
                <tr>
                  <th
                    className={[
                      headerBgClass,
                      "font-semibold text-left sticky left-0 border-b",
                      headerBorderClass,
                      "px-2 py-1",
                      stickyShadow,
                      "z-20",
                    ].join(" ")}
                  >
                    Driver
                  </th>
                  {filteredDrivers.map((d) => (
                    <th
                      key={`h2h-col-${d}`}
                      className={[
                        headerBgClass,
                        "font-semibold border-b",
                        headerBorderClass,
                        "text-center px-2 py-1 whitespace-nowrap",
                      ].join(" ")}
                      title={d}
                    >
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredDrivers.map((rd, i) => {
                  const rowBg = i % 2 ? rowEvenClass : rowOddClass;
                  return (
                    <tr
                      key={`row-${rd}`}
                      className={[rowBg, hoverRowClass, "transition-colors"].join(" ")}
                    >
                      <td
                        className={[
                          "font-medium sticky left-0 px-2 py-1 border-r",
                          rowBg,
                          isDark ? "border-slate-800/80" : "border-gray-200",
                          stickyShadow,
                          "z-10",
                        ].join(" ")}
                      >
                        {rd}
                      </td>

                      {filteredDrivers.map((cd) => {
                        const v = rd === cd ? "" : grid?.[rd]?.[cd];
                        const p = Number.isFinite(v)
                          ? v
                          : Number.isFinite(grid?.[cd]?.[rd])
                          ? 100 - grid[cd][rd]
                          : NaN;

                        let bg = "";
                        if (Number.isFinite(p)) {
                          // subtle green/red wash (50 neutral)
                          const t = Math.max(0, Math.min(100, p));
                          const g = Math.round(255 * Math.max(0, (t - 50) / 50));
                          const r = Math.round(255 * Math.max(0, (50 - t) / 50));
                          bg = `rgba(${r}, ${g}, 0, 0.12)`;
                        }

                        const isPick = (rd === A && cd === B) || (rd === B && cd === A);

                        return (
                          <td
                            key={`cell-${rd}-${cd}`}
                            className={[
                              "text-center border-b tabular-nums px-2 py-1",
                              isDark ? "border-slate-800/80" : "border-gray-100",
                              isPick ? "ring-2 ring-sky-400 ring-inset" : "",
                              rd !== cd ? "cursor-pointer" : "",
                            ].join(" ")}
                            style={{
                              backgroundColor: Number.isFinite(p) ? bg : undefined,
                            }}
                            title={Number.isFinite(p) ? `${rd} vs ${cd}: ${p.toFixed(1)}%` : ""}
                            onClick={() => {
                              if (rd !== cd) {
                                setA(rd);
                                setB(cd);
                              }
                            }}
                          >
                            {rd === cd ? "—" : Number.isFinite(p) ? `${p.toFixed(1)}%` : ""}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={"mt-2 text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
            Color: green = better for the row driver. 50% is neutral. Tap a cell to set A/B.
          </div>
        </>
      )}
    </section>
  );
}

function BetCalcTab({
  series,
  MAIN,
  TEAM_WINS,
  MFG_WINS,
  TOP_FORD,
  TOP_CHEVY,
  TOP_TOYOTA,
  RAMSource,
  H2H,
  FINISH,
  cardClass,
  isDark,
}) {
  const isTrucks = String(series || "").toLowerCase() === "trucks";

  const { data: mainRaw } = useJson(MAIN);
  const { data: teamRaw } = useJson(TEAM_WINS);
  const { data: mfgRaw } = useJson(MFG_WINS);
  const { data: fordRaw } = useJson(TOP_FORD);
  const { data: chevyRaw } = useJson(TOP_CHEVY);
  const { data: toyotaRaw } = useJson(TOP_TOYOTA);
  const { data: ramRaw } = useJson(isTrucks ? RAMSource : null);
  const { data: h2hRaw } = useJson(H2H);
  const { data: finishRaw } = useJson(FINISH);

  const normTop = (rawRows, driverKey, projKey) =>
    (Array.isArray(rawRows) ? rawRows : rawRows?.rows || [])
      .map((r) => ({
        Driver: String(r?.[driverKey] ?? "").trim(),
        Proj: r?.[projKey],
      }))
      .filter((r) => r.Driver);

const mainRows = useMemo(
  () => (Array.isArray(mainRaw) ? mainRaw : mainRaw?.rows || []),
  [mainRaw]
);

const teamRows = useMemo(() => {
  const raw = Array.isArray(teamRaw) ? teamRaw : teamRaw?.rows || [];
  if (!raw.length) return [];

  const has5 = raw.some((r) => r && ("Proj_5" in r || "Fair Odds_5" in r));
  const has4 = raw.some((r) => r && ("Proj_4" in r || "Fair Odds_4" in r));

  return raw
    .map((r) => {
      const proj = has5 ? r?.Proj_5 : has4 ? r?.Proj_4 : r?.Proj;
      const fair = has5 ? r?.["Fair Odds_5"] : has4 ? r?.["Fair Odds_4"] : r?.["Fair Odds"];

      return {
        Team: String(r?.Team ?? "").trim(),
        Proj: proj,
        "Fair Odds": fair,
      };
    })
    .filter((r) => r.Team);
}, [teamRaw]);

const mfgRows = useMemo(
  () => (Array.isArray(mfgRaw) ? mfgRaw : mfgRaw?.rows || []),
  [mfgRaw]
);

// ✅ NORMALIZED MANUFACTURERS
const fordRows = useMemo(
  () => normTop(fordRaw, "Ford Drivers", "Proj_1"),
  [fordRaw]
);

const chevyRows = useMemo(
  () => normTop(chevyRaw, "Chevrolet Drivers", "Proj_2"),
  [chevyRaw]
);

const toyotaRows = useMemo(
  () => normTop(toyotaRaw, "Toyota Drivers", "Proj_3"),
  [toyotaRaw]
);

const ramRows = useMemo(
  () => normTop(ramRaw, "RAM Drivers", "Proj_4"),
  [ramRaw]
);

const finishRows = useMemo(
  () => (Array.isArray(finishRaw) ? finishRaw : finishRaw?.rows || []),
  [finishRaw]
);

  // H2H normalize → list of drivers + lookup matrix (same idea as your H2H tool)
  const h2hRows = useMemo(() => (Array.isArray(h2hRaw) ? h2hRaw : h2hRaw?.rows || []), [h2hRaw]);

  const h2hDrivers = useMemo(() => {
    const set = new Set();
    for (const r of h2hRows) if (r?.Driver) set.add(String(r.Driver));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [h2hRows]);

  const h2hGrid = useMemo(() => {
    const g = {};
    for (const r of h2hRows) {
      const d = String(r?.Driver ?? "");
      if (!d) continue;
      g[d] = g[d] || {};
      for (const [k, v] of Object.entries(r || {})) {
        if (k === "Driver") continue;
        const n = Number(String(v ?? "").replace(/[,%\s]/g, ""));
        if (Number.isFinite(n)) g[d][k] = n; // stored as pct
      }
    }
    return g;
  }, [h2hRows]);

  const getH2HProbPct = useCallback(
    (a, b) => {
      if (!a || !b || a === b) return NaN;
      const direct = h2hGrid?.[a]?.[b];
      if (Number.isFinite(direct)) return direct;
      const back = h2hGrid?.[b]?.[a];
      if (Number.isFinite(back)) return 100 - back;
      return NaN;
    },
    [h2hGrid]
  );

  // Finish dist helpers
  const finishDrivers = useMemo(() => {
    const set = new Set();
    for (const r of finishRows) if (r?.Driver) set.add(String(r.Driver));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [finishRows]);

  const finishRowByDriver = useMemo(() => {
    const m = new Map();
    for (const r of finishRows) if (r?.Driver) m.set(String(r.Driver), r);
    return m;
  }, [finishRows]);

  const finishUnderPct = useCallback((row, line, maxPos = 40) => {
    if (!row) return NaN;
    const cutoff = Math.floor(line);
    let s = 0;
    for (let i = 1; i <= Math.min(maxPos, cutoff); i++) {
      const key1 = `P${i}`;
      const key2 = String(i);
      const v = Number(row?.[key1] ?? row?.[key2]);
      if (Number.isFinite(v)) s += v;
    }
    return Math.max(0, Math.min(100, s));
  }, []);

  // ---------------- UI state ----------------
  const MARKET_GROUPS = useMemo(() => {
    const groups = [
      {
        id: "driver",
        label: "Driver Markets",
        rows: mainRows,
        rowLabelKey: "Driver",
        markets: [
          { id: "win", label: "Win", probKey: "Win%" },
          { id: "t3", label: "Top 3", probKey: "T3%" },
          { id: "t5", label: "Top 5", probKey: "T5%" },
          { id: "t10", label: "Top 10", probKey: "T10%" },
          { id: "t20", label: "Top 20", probKey: "T20%" },
        ],
      },
      {
        id: "team",
        label: "Winning Team",
        rows: teamRows,
        rowLabelKey: "Team",
        markets: [{ id: "team_win", label: "Win (Team)", probKey: "Proj" }],
      },
      {
        id: "mfg",
        label: "Winning Manufacturer",
        rows: mfgRows,
        rowLabelKey: "Manufacturer",
        markets: [{ id: "mfg_win", label: "Win (Manufacturer)", probKey: "Proj" }],
      },
      {
        id: "top_ford",
        label: "Top Ford Driver",
        rows: fordRows,
        rowLabelKey: "Driver",
        markets: [{ id: "top_ford", label: "Top Ford Driver", probKey: "Proj" }],
      },
      {
        id: "top_chevy",
        label: "Top Chevrolet Driver",
        rows: chevyRows,
        rowLabelKey: "Driver",
        markets: [{ id: "top_chevy", label: "Top Chevrolet Driver", probKey: "Proj" }],
      },
      {
        id: "top_toyota",
        label: "Top Toyota Driver",
        rows: toyotaRows,
        rowLabelKey: "Driver",
        markets: [{ id: "top_toyota", label: "Top Toyota Driver", probKey: "Proj" }],
      },
    ];

    if (isTrucks) {
      groups.push({
        id: "top_ram",
        label: "Top RAM Driver",
        rows: ramRows,
        rowLabelKey: "Driver",
        markets: [{ id: "top_ram", label: "Top RAM Driver", probKey: "Proj" }],
      });
    }

    // H2H + Finish as “special” groups (not row/market keyed)
    groups.push({
      id: "h2h",
      label: "H2H",
      rows: [],
      rowLabelKey: null,
      markets: [{ id: "h2h", label: "Driver vs Driver", probKey: null }],
    });
    groups.push({
      id: "finish_ou",
      label: "Finish O/U (from Finish Dist)",
      rows: [],
      rowLabelKey: null,
      markets: [{ id: "finish_ou", label: "Under / Over Line", probKey: null }],
    });

    return groups;
  }, [mainRows, teamRows, mfgRows, fordRows, chevyRows, toyotaRows, ramRows, isTrucks]);

  const [groupId, setGroupId] = useState("driver");
  const group = useMemo(() => MARKET_GROUPS.find((g) => g.id === groupId) || MARKET_GROUPS[0], [MARKET_GROUPS, groupId]);

  const [marketId, setMarketId] = useState(group?.markets?.[0]?.id || "");
  useEffect(() => {
    const first = group?.markets?.[0]?.id || "";
    setMarketId(first);
  }, [groupId]); // switch market when group changes

  // row selection (for normal groups)
  const rowOptions = useMemo(() => {
    if (!group?.rows?.length || !group?.rowLabelKey) return [];
    const key = group.rowLabelKey;
    return group.rows
      .map((r) => String(r?.[key] ?? "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [group]);

  const [entity, setEntity] = useState("");
  useEffect(() => {
    if (!rowOptions.length) {
      setEntity("");
      return;
    }
    if (!entity || !rowOptions.includes(entity)) setEntity(rowOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowOptions]);

  // H2H selections
  const [h2hA, setH2hA] = useState("");
  const [h2hB, setH2hB] = useState("");
  useEffect(() => {
    if (!h2hDrivers.length) return;
    if (!h2hA) setH2hA(h2hDrivers[0]);
    if (!h2hB) setH2hB(h2hDrivers[1] || h2hDrivers[0]);
  }, [h2hDrivers, h2hA, h2hB]);

  // Finish O/U selections
  const [finishDriver, setFinishDriver] = useState("");
  const [finishLine, setFinishLine] = useState(10.5);
  useEffect(() => {
    if (!finishDrivers.length) return;
    if (!finishDriver) setFinishDriver(finishDrivers[0]);
  }, [finishDrivers, finishDriver]);

  // Betting inputs
  const [bookOdds, setBookOdds] = useState("");
  const [bankrollUnits, setBankrollUnits] = useState(100);
  const [unitSizeDollars, setUnitSizeDollars] = useState(10);
  const [kellyMult, setKellyMult] = useState(0.25); // quarter-kelly default
  const [maxUnitsCap, setMaxUnitsCap] = useState(5); // optional “don’t go crazy” cap in units

  // ---------------- probability selection ----------------
  const selectedRow = useMemo(() => {
    if (!group?.rows?.length || !group?.rowLabelKey || !entity) return null;
    const key = group.rowLabelKey;
    return group.rows.find((r) => String(r?.[key] ?? "").trim() === entity) || null;
  }, [group, entity]);

  const selectedMarket = useMemo(() => group?.markets?.find((m) => m.id === marketId) || null, [group, marketId]);

  const modelProbPct = useMemo(() => {
    if (groupId === "h2h") return getH2HProbPct(h2hA, h2hB);
    if (groupId === "finish_ou") {
      const row = finishRowByDriver.get(finishDriver);
      return finishUnderPct(row, finishLine, 40); // “Under” probability
    }
    if (!selectedRow || !selectedMarket?.probKey) return NaN;
    return pctNumber(selectedRow?.[selectedMarket.probKey]); // returns 0..100
  }, [
    groupId,
    selectedRow,
    selectedMarket,
    h2hA,
    h2hB,
    getH2HProbPct,
    finishDriver,
    finishLine,
    finishRowByDriver,
    finishUnderPct,
  ]);

  const modelP = useMemo(() => {
    const p = Number(modelProbPct) / 100;
    return Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : NaN;
  }, [modelProbPct]);

  // book math
  const o = useMemo(() => parseAmericanOdds(bookOdds), [bookOdds]);
  const impliedPct = useMemo(() => americanToImpliedProbPct(o), [o]);
  const dec = useMemo(() => americanToDecimal(o), [o]);

  const edgePct = useMemo(() => {
    if (!Number.isFinite(modelProbPct) || !Number.isFinite(impliedPct)) return NaN;
    return modelProbPct - impliedPct;
  }, [modelProbPct, impliedPct]);

  const fullKelly = useMemo(() => kellyFraction(modelP, dec), [modelP, dec]);
  const adjKelly = useMemo(() => (Number.isFinite(fullKelly) ? fullKelly * kellyMult : 0), [fullKelly, kellyMult]);

  const suggestedUnits = useMemo(() => {
    const roll = Number(bankrollUnits);
    if (!(roll > 0) || !(adjKelly > 0)) return 0;
    const u = adjKelly * roll;
    const cap = Number(maxUnitsCap);
    return Number.isFinite(cap) && cap > 0 ? Math.min(u, cap) : u;
  }, [adjKelly, bankrollUnits, maxUnitsCap]);

  const suggestedDollars = useMemo(() => {
    const unitSize = Number(unitSizeDollars);
    if (!(suggestedUnits > 0) || !(unitSize > 0)) return 0;
    return suggestedUnits * unitSize;
  }, [suggestedUnits, unitSizeDollars]);

  // EV per 1 unit risked (risk=1)
  const evPerUnit = useMemo(() => {
    if (!(modelP > 0 && modelP < 1) || !(dec > 1)) return NaN;
    const b = dec - 1; // profit if win
    return modelP * b - (1 - modelP) * 1;
  }, [modelP, dec]);

  const heading = useMemo(() => {
    if (groupId === "h2h") return `${h2hA || "—"} over ${h2hB || "—"}`;
    if (groupId === "finish_ou") return `${finishDriver || "—"} — Under ${finishLine.toFixed(1)}`;
    if (!group?.label) return "—";
    return `${entity || "—"} — ${selectedMarket?.label || "—"}`;
  }, [groupId, h2hA, h2hB, finishDriver, finishLine, group, entity, selectedMarket]);

  const pill = (txt) => (
    <span
      className={
        "text-xs px-2 py-1 rounded-full border " +
        (isDark ? "border-slate-700/70 bg-slate-950/60 text-slate-200" : "border-slate-200 bg-white text-slate-800")
      }
    >
      {txt}
    </span>
  );

  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-2">
        <h2 className="text-sm font-semibold">Bet Calc</h2>
        <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-600")}>
          Model % → compare to book odds → Kelly units
        </div>
      </div>

      <div className={cardClass + " p-4"}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: selectors */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Data Bucket</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className={
                  "w-full border rounded-md px-3 py-2 text-sm " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                }
              >
                {MARKET_GROUPS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Market select (for non-special groups still shows 1 option, that’s fine) */}
            <div>
              <label className="block text-xs font-semibold mb-1">Market</label>
              <select
                value={marketId}
                onChange={(e) => setMarketId(e.target.value)}
                className={
                  "w-full border rounded-md px-3 py-2 text-sm " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                }
              >
                {group?.markets?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Entity picker */}
            {groupId !== "h2h" && groupId !== "finish_ou" && (
              <div>
                <label className="block text-xs font-semibold mb-1">Select</label>
                <select
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  className={
                    "w-full border rounded-md px-3 py-2 text-sm " +
                    (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                  }
                >
                  {rowOptions.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* H2H picker */}
            {groupId === "h2h" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-semibold mb-1">Driver A</label>
                  <select
                    value={h2hA}
                    onChange={(e) => setH2hA(e.target.value)}
                    className={
                      "w-full border rounded-md px-3 py-2 text-sm " +
                      (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                    }
                  >
                    {h2hDrivers.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Driver B</label>
                  <select
                    value={h2hB}
                    onChange={(e) => setH2hB(e.target.value)}
                    className={
                      "w-full border rounded-md px-3 py-2 text-sm " +
                      (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                    }
                  >
                    {h2hDrivers.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Finish O/U picker */}
            {groupId === "finish_ou" && (
              <div className="space-y-2">
                <div>
                  <label className="block text-xs font-semibold mb-1">Driver</label>
                  <select
                    value={finishDriver}
                    onChange={(e) => setFinishDriver(e.target.value)}
                    className={
                      "w-full border rounded-md px-3 py-2 text-sm " +
                      (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                    }
                  >
                    {finishDrivers.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold">Finish Line: {finishLine.toFixed(1)}</label>
                    <div className={"text-[11px] " + (isDark ? "text-slate-400" : "text-gray-600")}>
                      Under = {finishLine.toFixed(1)} or better
                    </div>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={40}
                    step={0.5}
                    value={finishLine}
                    onChange={(e) => setFinishLine(Number(e.target.value))}
                    className="w-full accent-sky-400"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Middle: inputs */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold mb-1">Sportsbook Odds (American)</label>
              <input
                value={bookOdds}
                onChange={(e) => setBookOdds(e.target.value)}
                placeholder="+450 or -120"
                className={
                  "w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                  (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                }
              />
              <div className={"mt-1 text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
                We convert odds → implied % automatically.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-semibold mb-1">Bankroll (Units)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={bankrollUnits}
                  onChange={(e) => setBankrollUnits(Number(e.target.value))}
                  className={
                    "w-full border rounded-md px-3 py-2 text-sm " +
                    (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Unit Size ($)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={unitSizeDollars}
                  onChange={(e) => {
                    // Keep the raw dollar value while typing so "50" remains 50,
                    // rather than being interpreted as a fractional value.
                    const next = e.target.value.replace(/[^0-9.]/g, "");
                    const parts = next.split(".");
                    const cleaned =
                      parts.length <= 1
                        ? next
                        : `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
                    setUnitSizeDollars(cleaned);
                  }}
                  onBlur={() => {
                    const n = Number(unitSizeDollars);
                    setUnitSizeDollars(Number.isFinite(n) && n >= 0 ? String(n) : "");
                  }}
                  placeholder="25.00"
                  className={
                    "w-full border rounded-md px-3 py-2 text-sm " +
                    (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Max Bet Cap (Units)</label>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  value={maxUnitsCap}
                  onChange={(e) => setMaxUnitsCap(Number(e.target.value))}
                  className={
                    "w-full border rounded-md px-3 py-2 text-sm " +
                    (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900")
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">
                Kelly Multiplier: {(kellyMult * 100).toFixed(0)}% (quarter-kelly default)
              </label>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={kellyMult}
                onChange={(e) => setKellyMult(Number(e.target.value))}
                className="w-full accent-sky-400"
              />
              <div className={"mt-1 text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
                1.00 = full Kelly · 0.25 = quarter Kelly (usually smoother)
              </div>
            </div>
          </div>

          {/* Right: outputs */}
          <div className="space-y-3">
            <div className={"rounded-2xl border p-4 " + (isDark ? "border-slate-800 bg-slate-900/80" : "border-slate-200 bg-white")}>
              <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-600")}>Selected</div>
              <div className="text-lg font-extrabold mt-1">{heading}</div>

              <div className="mt-3 flex flex-wrap gap-2">
                {pill(`Model: ${fmtPct1(modelProbPct)}`)}
                {pill(`Book implied: ${fmtPct1(impliedPct)}`)}
                {pill(`Edge: ${Number.isFinite(edgePct) ? `${edgePct >= 0 ? "+" : ""}${edgePct.toFixed(1)}%` : "—"}`)}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <div className={"text-[11px] " + (isDark ? "text-slate-400" : "text-gray-600")}>American Odds</div>
                  <div className="text-base font-bold">{Number.isFinite(o) ? formatOdds(o) : "—"}</div>
                </div>
                <div className={isDark ? "text-slate-300" : "text-slate-700"}>
                  <div className={"text-[11px] " + (isDark ? "text-slate-400" : "text-gray-600")}>EV / 1u risk</div>
                  <div className="text-base font-bold">
                    {Number.isFinite(evPerUnit) ? evPerUnit.toFixed(3) : "—"}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t " style={{ borderColor: isDark ? "rgba(51,65,85,0.65)" : "rgba(226,232,240,1)" }}>
                <div className={"text-[11px] " + (isDark ? "text-slate-400" : "text-gray-600")}>Kelly</div>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div className="text-3xl font-extrabold leading-none">
                      {suggestedUnits > 0 ? suggestedUnits.toFixed(2) : "0.00"}u
                    </div>
                    <div className="text-xl font-bold mt-1">
                      {suggestedDollars > 0
                        ? suggestedDollars.toLocaleString(undefined, {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "$0.00"}
                    </div>
                    <div className={"text-[11px] mt-1 " + (isDark ? "text-slate-500" : "text-gray-500")}>
                      At ${Number(unitSizeDollars || 0).toFixed(2)} per unit · Full Kelly: {fmtPct2(fullKelly * 100)} · Adjusted: {fmtPct2(adjKelly * 100)}
                    </div>
                  </div>
                  <div className={"text-right text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
                    If edge ≤ 0 → bet 0u
                  </div>
                </div>
              </div>
            </div>

            <div className={"text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
              Note: implied % includes vig; your model % does not.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------- Finish Distribution Table (click → chart) ---------------------- */
function FinishDistTable({
  source,
  maxHeight = "70vh",
  theme = "dark",
  cardClass,
  headerBgClass,
  headerBorderClass,
  rowOddClass,
  rowEvenClass,
  isDark,
}) {
  const { data, err, loading } = useJson(source);

  const rows = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }, [data]);

  const allCols = useMemo(() => (rows.length ? Object.keys(rows[0]) : []), [rows]);

  const posCols = useMemo(() => {
    const ps = allCols
      .filter((c) => /^P?\d+$/i.test(String(c)))
      .map((c) => {
        const m = String(c).match(/\d+/);
        return { src: c, n: m ? Number(m[0]) : 0 };
      })
      .sort((a, b) => a.n - b.n)
      .map((x) => x.src);

    const other = allCols.filter((c) => c !== "Driver" && !ps.includes(c));
    return ["Driver", ...ps, ...other];
  }, [allCols]);

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => posCols.some((c) => String(r?.[c] ?? "").toLowerCase().includes(s)));
  }, [rows, posCols, q]);

  // heat stats for P# columns: higher better
  const heatStats = useMemo(() => {
    const stats = {};
    if (!filtered.length) return stats;
    for (const c of posCols) {
      if (c === "Driver") continue;
      const dir = /^P?\d+$/i.test(c) ? "higher" : dirForCol(c);
      if (!dir) continue;
      let min = Infinity,
        max = -Infinity;
      for (const r of filtered) {
        const n = num(r?.[c]);
        if (Number.isFinite(n)) {
          if (n < min) min = n;
          if (n > max) max = n;
        }
      }
      if (min !== Infinity && max !== -Infinity) stats[c] = { min, max, dir };
    }
    return stats;
  }, [filtered, posCols]);


const defaultP1Col = useMemo(() => {
  const exact = posCols.find((c) => /^P?1$/i.test(String(c)));
  return exact || posCols[1] || "";
}, [posCols]);

const [sort, setSort] = useState({ col: "", dir: "desc" });

useEffect(() => {
  if (!defaultP1Col) return;
  if (!sort.col || !posCols.includes(sort.col)) setSort({ col: defaultP1Col, dir: "desc" });
}, [defaultP1Col, posCols, sort.col]);
  const sorted = useMemo(() => {
    if (!sort.col) return filtered;
    return [...filtered].sort((a, b) => cmp(a?.[sort.col], b?.[sort.col], sort.dir));
  }, [filtered, sort]);

  const fmtPct = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `${n.toFixed(1)}%` : "";
  };

  const colWidthCh = useMemo(() => {
    const widths = {};
    const sample = rows.slice(0, 150);
    for (const c of posCols) {
      let maxLen = c.length;
      for (const r of sample) maxLen = Math.max(maxLen, String(r?.[c] ?? "").length);
      widths[c] = c === "Driver" ? clamp(Math.max(maxLen, 16), 14, 26) : 6;
    }
    return widths;
  }, [rows, posCols]);

  const exportCSV = () => {
    const cols = posCols;
    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of sorted) {
      lines.push(
        cols
          .map((c) => {
            const v = r?.[c];
            return escapeCSV(c === "Driver" ? v : Number.isFinite(+v) ? (+v).toFixed(1) : "");
          })
          .join(",")
      );
    }
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `finish_distribution_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const densityRow = "px-2 py-1 text-xs";
  const densityHead = "px-2 py-1 text-xs";

  const [selected, setSelected] = useState(null);
  const posKeys = useMemo(
    () => posCols.filter((c) => c !== "Driver" && /^P?\d+$/i.test(String(c))),
    [posCols]
  );

  const chartData = useMemo(() => {
    if (!selected) return [];
    return posKeys.map((c) => {
      const n = Number(String(c).replace(/[^\d]/g, ""));
      const v = Number(selected?.[c]);
      return { pos: n, pct: Number.isFinite(v) ? Number(v.toFixed(1)) : 0 };
    });
  }, [selected, posKeys]);

  const expectedFinish = useMemo(() => {
    if (!chartData.length) return NaN;
    let wsum = 0,
      psum = 0;
    for (const { pos, pct } of chartData) {
      if (pct > 0) {
        const p = pct / 100.0;
        wsum += pos * p;
        psum += p;
      }
    }
    return psum > 0 ? wsum / psum : NaN;
  }, [chartData]);

  const hoverRowClass = isDark
    ? "hover:bg-sky-900/40 cursor-pointer"
    : "hover:bg-blue-50/60 cursor-pointer";

  const cellBorder = isDark ? "border-b border-slate-800/80" : "border-b border-gray-100";

  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-2">
        <h2 className="text-sm font-semibold">Finish Distribution</h2>
        <div className="ml-auto flex items-center gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search drivers…"
            className={
              "border rounded-full px-3 py-2 w-48 sm:w-56 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
              (isDark
                ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
            }
          />
          <button
            className="h-8 px-3 text-xs rounded-md bg-sky-500 text-slate-950 font-semibold hover:bg-sky-400 shadow-sm"
            onClick={exportCSV}
          >
            Export CSV
          </button>
          <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-500")}>
            {sorted.length.toLocaleString()} rows
          </div>
        </div>
      </div>

      {selected && (
        <div className={cardClass + " mb-4 p-3"}>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-lg font-bold">{selected.Driver}</div>
            <div className={"text-xs " + (isDark ? "text-slate-400" : "text-gray-600")}>
              Expected finish: {Number.isFinite(expectedFinish) ? expectedFinish.toFixed(2) : "—"}
            </div>
            <button
              className={
                "ml-auto px-2 py-1 text-xs rounded-full border " +
                (isDark
                  ? "bg-slate-900/80 text-slate-100 hover:bg-slate-800 border-slate-700/80"
                  : "bg-white text-slate-900 hover:bg-gray-50 border-gray-300")
              }
              onClick={() => setSelected(null)}
              title="Close chart"
            >
              Close
            </button>
          </div>

          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={isDark ? "rgba(148, 163, 184, 0.35)" : "rgba(148, 163, 184, 0.35)"}
                />
                <XAxis
                  dataKey="pos"
                  tickFormatter={(t) => `P${t}`}
                  interval={0}
                  height={40}
                  tick={{
                    fill: isDark ? "#e5e7eb" : "#020617",
                    fontSize: 11,
                  }}
                  axisLine={{ stroke: isDark ? "#64748b" : "#94a3b8" }}
                  tickLine={{ stroke: isDark ? "#64748b" : "#94a3b8" }}
                />
                <YAxis
                  unit="%"
                  tickFormatter={(t) => t.toFixed(0)}
                  tick={{
                    fill: isDark ? "#e5e7eb" : "#020617",
                    fontSize: 11,
                  }}
                  axisLine={{ stroke: isDark ? "#64748b" : "#94a3b8" }}
                  tickLine={{ stroke: isDark ? "#64748b" : "#94a3b8" }}
                />
                <Tooltip
                  formatter={(v) => [`${Number(v).toFixed(1)}%`, "Probability"]}
                  labelFormatter={(l) => `Position P${l}`}
                  contentStyle={{
                    backgroundColor: isDark ? "#020617" : "#ffffff",
                    border: "1px solid rgba(148,163,184,0.6)",
                    color: isDark ? "#e5e7eb" : "#020617",
                    fontSize: 12,
                  }}
                />
                <Bar
                  dataKey="pct"
                  name="Finish %"
                  fill={isDark ? "#38bdf8" : "#0f172a"}
                  stroke={isDark ? "#7dd3fc" : "#1f2937"}
                  radius={[3, 3, 0, 0]}
                />
                {Number.isFinite(expectedFinish) && (
                  <ReferenceLine
                    x={Number(expectedFinish.toFixed(2))}
                    stroke={isDark ? "#38bdf8" : "#3b82f6"}
                    strokeDasharray="4 4"
                    label={{
                      value: `EF ${expectedFinish.toFixed(2)}`,
                      position: "top",
                      fill: isDark ? "#38bdf8" : "#3b82f6",
                      fontSize: 12,
                    }}
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <FinishPositionOU row={selected} maxPos={40} isDark={isDark} />

          <div className={"text-[11px] mt-1 " + (isDark ? "text-slate-500" : "text-gray-500")}>
            Bars show probability of each exact finishing position. The dashed line marks the expected finish.
          </div>
        </div>
      )}

      {loading && (
        <div className={isDark ? "text-sm text-slate-400" : "text-sm text-gray-600"}>Loading…</div>
      )}
      {err && (
        <div className={isDark ? "text-sm text-red-400" : "text-sm text-red-600"}>
          Failed to load: {String(err)}
        </div>
      )}

      {!loading && !err && (
        <>
          <div className={cardClass + " overflow-auto"} style={{ maxHeight }}>
            <table className="w-full table-fixed border-separate" style={{ borderSpacing: 0 }}>
              <colgroup>
                {posCols.map((c) => (
                  <col key={c} style={{ width: `${colWidthCh[c] ?? 8}ch` }} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  {posCols.map((c) => {
                    const active = sort.col === c;
                    const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "▲";
                    return (
                      <th
                        key={c}
                        onClick={() =>
                          setSort((s) =>
                            s.col === c
                              ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                              : { col: c, dir: "desc" }
                          )
                        }
                        className={[
                          // ✅ FIX: reduce z-index so it never beats mobile menu overlays
                          "sticky top-0 z-10",
                          headerBgClass,
                          "font-semibold border-b",
                          headerBorderClass,
                          c === "Driver" ? "text-left" : "text-center",
                          densityHead,
                        ].join(" ")}
                        title={c}
                      >
                        <span className="inline-flex items-center gap-1">
                          {c} <span className="opacity-60 text-[10px]">{arrow}</span>
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {sorted.map((r, i) => {
                  const rowBg = i % 2 ? rowEvenClass : rowOddClass;
                  return (
                    <tr
                      key={i}
                      className={[rowBg, hoverRowClass].join(" ")}
                      onClick={() => setSelected(r)}
                      title="Click to show chart"
                    >
                      {posCols.map((c) => {
                        const stat = heatStats[c];
                        const textColor = stat
                          ? heatTextColor(stat.min, stat.max, num(r?.[c]), stat.dir, theme)
                          : null;
                        return (
                          <td
                            key={c}
                            className={[
                              cellBorder,
                              "tabular-nums",
                              c === "Driver" ? "text-left" : "text-center",
                              densityRow,
                            ].join(" ")}
                          >
                            {c === "Driver" ? (
                              r?.Driver ?? ""
                            ) : (
                              <span
                                style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                              >
                                {fmtPct(r?.[c])}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {!sorted.length && (
                  <tr>
                    <td
                      className={"px-2 py-2 text-xs " + (isDark ? "text-slate-500" : "text-gray-500")}
                      colSpan={posCols.length}
                    >
                      No rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={"mt-2 text-[11px] " + (isDark ? "text-slate-500" : "text-gray-500")}>
            Click a driver row to see a histogram of their projected outcomes. Numbers are already in percent (0–100).
          </div>
        </>
      )}
    </section>
  );
}

/* ----------------------------- page with Tabs (hub) ----------------------------- */

export default function NascarBetting({ series = "cup" }) {
  const BASE = import.meta?.env?.BASE_URL ?? "/";

  const key = String(series || "cup").toLowerCase();
  const seriesPath = key === "xfinity" ? "xfinity" : key === "trucks" ? "trucks" : "cup";

  const seriesTitle =
    key === "xfinity" ? "NASCAR O'Reilly" : key === "trucks" ? "NASCAR Trucks" : "NASCAR Cup";

  const MAIN = `${BASE}data/nascar/${seriesPath}/latest/odds_main.json`;
  const EXTRA = `${BASE}data/nascar/${seriesPath}/latest/odds.json`;
  const MFG_WINS = `${BASE}data/nascar/${seriesPath}/latest/manufacturer_wins.json`;
  const TOP_FORD = `${BASE}data/nascar/${seriesPath}/latest/manufacturer_top_ford.json`;
  const TOP_CHEVY = `${BASE}data/nascar/${seriesPath}/latest/manufacturer_top_chevy.json`;
  const TOP_TOYOTA = `${BASE}data/nascar/${seriesPath}/latest/manufacturer_top_toyota.json`;
  const RAMSource = `${BASE}data/nascar/trucks/latest/manufacturer_top_ram.json`;
  const TEAM_WINS = `${BASE}data/nascar/${seriesPath}/latest/team_wins.json`;
  const H2H = `${BASE}data/nascar/${seriesPath}/latest/h2h_matrix.json`;
  const FINISH = `${BASE}data/nascar/${seriesPath}/latest/finish_dist.json`;

  // meta.json fallbacks (optional on your server)
  const MAIN_META = MAIN.replace(/odds_main\.json$/, "meta.json");
  const EXTRA_META = EXTRA.replace(/odds\.json$/, "meta.json");
  const MFG_WINS_META = MFG_WINS.replace(/manufacturer_wins\.json$/, "meta.json");
  const TOP_FORD_META = TOP_FORD.replace(/manufacturer_top_ford\.json$/, "meta.json");
  const TOP_CHEVY_META = TOP_CHEVY.replace(/manufacturer_top_chevy\.json$/, "meta.json");
  const TOP_TOYOTA_META = TOP_TOYOTA.replace(/manufacturer_top_toyota\.json$/, "meta.json");
  const TEAM_WINS_META = TEAM_WINS.replace(/team_wins\.json$/, "meta.json");
  const H2H_META = H2H.replace(/h2h_matrix\.json$/, "meta.json");
  const FINISH_META = FINISH.replace(/finish_dist\.json$/, "meta.json");

  // collect freshest timestamp across all tabs
  const upMain = useLastUpdated(MAIN, MAIN_META);
  const upExtra = useLastUpdated(EXTRA, EXTRA_META);
  const upMfgWins = useLastUpdated(MFG_WINS, MFG_WINS_META);
    const upTopFord = useLastUpdated(TOP_FORD, TOP_FORD_META);
  const upTopChevy = useLastUpdated(TOP_CHEVY, TOP_CHEVY_META);
  const upTopToyota = useLastUpdated(TOP_TOYOTA, TOP_TOYOTA_META);
  const upTeamWins = useLastUpdated(TEAM_WINS, TEAM_WINS_META);
  const upH2H = useLastUpdated(H2H, H2H_META);
  const upFinish = useLastUpdated(FINISH, FINISH_META);
  const updatedAt = useMemo(() => {
    const ds = [upMain, upExtra, upMfgWins, upTopFord, upTopChevy, upTopToyota, upTeamWins, upH2H, upFinish].filter(Boolean).map((d) => +d);
    return ds.length ? new Date(Math.max(...ds)) : null;
  }, [upMain, upExtra, upMfgWins, upTopFord, upTopChevy, upTopToyota, upTeamWins, upH2H, upFinish]);

  const TABS = [
    { id: "driver", label: "Driver Sims" },
    { id: "mfg", label: "Manufacturer Sims" },
    { id: "team", label: "Team Sims" },
    { id: "h2h", label: "H2H Tool" },
    { id: "finish", label: "Finish Dist" },
    { id: "betcalc", label: "Bet Calc" },
  ];

  const [active, setActive] = useState("driver");
  const [theme, setTheme] = useState("dark"); // "dark" | "light"
  const isDark = theme === "dark";

  // same theme/color system as projections
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  // IMPORTANT: make these fully opaque (no /95) so sticky header never shows through
  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-white text-slate-800";

  const headerBorderClass = isDark ? "border-slate-800/80" : "border-gray-200";

  // IMPORTANT: fully opaque row backgrounds so sticky columns are never transparent
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900" : "bg-gray-50";

  return (
    <div className={outerClass}>
      {/* ✅ FIX: isolate page content in a low z-index stacking context so mobile menu always wins */}
      <div className="relative z-0">
        <div className="px-4 md:px-6 py-5">
          <div className="w-full">
            {/* Page header and navigation */}
            <header className="mb-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h1 className="text-xl font-bold tracking-tight">{seriesTitle} — Betting Sims</h1>
                  <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedAt ? fmtUpdated(updatedAt) : "—"}</span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div role="group" aria-label="Color theme"
                    className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                      (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")}>
                    <button type="button" aria-pressed={isDark} onClick={() => setTheme("dark")}
                      className={"rounded-full px-3 py-1 font-medium transition " +
                        (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")}>Dark</button>
                    <button type="button" aria-pressed={!isDark} onClick={() => setTheme("light")}
                      className={"rounded-full px-3 py-1 font-medium transition " +
                        (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")}>Light</button>
                  </div>
                </div>
              </div>

              <div className={cardClass + " flex flex-wrap items-end justify-between gap-x-6 gap-y-3 px-4 py-3"}>
                <nav aria-label="NASCAR series" className={"flex flex-wrap items-center gap-5 border-b " +
                  (isDark ? "border-slate-800" : "border-slate-200")}>
                  {[
                    ["cup", "Cup"],
                    ["xfinity", "O'Reilly"],
                    ["trucks", "Trucks"],
                  ].map(([kSlug, label]) => (
                    <a key={kSlug}
                      href={`/nascar/${kSlug === "xfinity" ? "oreilly" : kSlug}/sims`}
                      aria-current={key === kSlug ? "page" : undefined}
                      className={"relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-xs font-semibold transition-colors " +
                        (key === kSlug
                          ? (isDark ? "text-white" : "text-slate-950") + " after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-0.5 after:rounded-full after:bg-sky-400"
                          : isDark ? "text-slate-400 hover:text-slate-100" : "text-slate-500 hover:text-slate-900")}
                    >{label}</a>
                  ))}
                </nav>

                <div className="min-w-0 max-w-full">
                  <div role="tablist" aria-label="Betting Sims view"
                    className={"inline-flex flex-wrap items-center gap-1 border-b " +
                      (isDark ? "border-slate-800" : "border-slate-200")}>
                    {TABS.map((t) => {
                      const on = active === t.id;
                      return (
                        <button key={t.id} type="button" role="tab" aria-selected={on}
                          onClick={() => setActive(t.id)}
                          className={"h-9 whitespace-nowrap border-b-2 px-2.5 text-xs font-semibold transition " +
                            (on ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900")}
                        >{t.label}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </header>

            {/* Tab content */}
            <div>
              {active === "driver" && (
                <BettingTableBlock
                  title="Betting Dashboard (Driver Sims)"
                  source={MAIN}
                  maxHeight="70vh"
                  theme={theme}
                  cardClass={cardClass}
                  headerBgClass={headerBgClass}
                  headerBorderClass={headerBorderClass}
                  rowOddClass={rowOddClass}
                  rowEvenClass={rowEvenClass}
                  isDark={isDark}
                />
              )}
              {active === "mfg" && (
                <ManufacturerSections
        series={series}
                  mfgWinsSource={MFG_WINS}
                  fordSource={TOP_FORD}
                  chevySource={TOP_CHEVY}
                  toyotaSource={TOP_TOYOTA}
        RAMSource={RAMSource}
                  fallbackSource={EXTRA}
                  cardClass={cardClass}
                  headerBgClass={headerBgClass}
                  headerBorderClass={headerBorderClass}
                  rowOddClass={rowOddClass}
                  rowEvenClass={rowEvenClass}
                  isDark={isDark}
                />
              )}
{active === "team" && (
  <TeamSections                  teamSource={TEAM_WINS}
                  mfgWinsSource={MFG_WINS}
                  fallbackSource={EXTRA}
                  cardClass={cardClass}
                  headerBgClass={headerBgClass}
                  headerBorderClass={headerBorderClass}
                  rowOddClass={rowOddClass}
                  rowEvenClass={rowEvenClass}
                  isDark={isDark}
                />
)}
              {active === "h2h" && (
                <H2HMatrixTool
                  source={H2H}
                  maxHeight="78vh"
                  cardClass={cardClass}
                  headerBgClass={headerBgClass}
                  headerBorderClass={headerBorderClass}
                  rowOddClass={rowOddClass}
                  rowEvenClass={rowEvenClass}
                  isDark={isDark}
                />
              )}
              {active === "finish" && (
                <FinishDistTable
                  source={FINISH}
                  maxHeight="70vh"
                  theme={theme}
                  cardClass={cardClass}
                  headerBgClass={headerBgClass}
                  headerBorderClass={headerBorderClass}
                  rowOddClass={rowOddClass}
                  rowEvenClass={rowEvenClass}
                  isDark={isDark}
                />
              )}
              {active === "betcalc" && (
              <BetCalcTab
                series={series}
                MAIN={MAIN}
                TEAM_WINS={TEAM_WINS}
                MFG_WINS={MFG_WINS}
                TOP_FORD={TOP_FORD}
                TOP_CHEVY={TOP_CHEVY}
                TOP_TOYOTA={TOP_TOYOTA}
                RAMSource={RAMSource}
                H2H={H2H}
                FINISH={FINISH}
                cardClass={cardClass}
                isDark={isDark}
              />
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
